import type { GridValue, MidiTimeSignature } from "./music.ts";
import type { BarRange, InputMethod, InputPassDraft } from "./input-pass.ts";

export type CaptureSessionMode = "NEW_CAPTURE" | "ADD_PASS" | "RHYTHM_ONLY" | "VOICE_ONLY" | "VOICE_TAP";
export type CaptureSessionStatus = "SETUP" | "COUNT_IN" | "PRE_ROLL" | "RECORDING" | "SECOND_PASS" | "STOPPING" | "COMPLETE" | "CANCELLED";

export type CaptureSession = {
  sessionId: string;
  mode: CaptureSessionMode;
  inputMethod: InputMethod;
  countInBeats: number;
  playbackStartTick: number;
  recordStartTick: number;
  recordEndTick: number | null;
  untilStop: boolean;
  preRollBars: number;
  pickupEnabled: boolean;
  replacementRange: BarRange | null;
  tempo: number;
  grid: GridValue;
  audioTakeId: string | null;
  rhythmTakeId: string | null;
  status: CaptureSessionStatus;
};

const ticksPerBar = (ppq: number, signature: MidiTimeSignature) =>
  Math.round(Math.max(1 / 8, signature.numerator * 4 / signature.denominator) * ppq);

export function createNewCaptureSession(options: {
  sessionId: string;
  mode?: "NEW_CAPTURE" | "RHYTHM_ONLY";
  inputMethod: InputMethod;
  tempo: number;
  grid: GridValue;
}): CaptureSession {
  return {
    sessionId: options.sessionId,
    mode: options.mode ?? "NEW_CAPTURE",
    inputMethod: options.inputMethod,
    countInBeats: 4,
    playbackStartTick: 0,
    recordStartTick: 0,
    recordEndTick: null,
    untilStop: true,
    preRollBars: 0,
    pickupEnabled: false,
    replacementRange: null,
    tempo: options.tempo,
    grid: options.grid,
    audioTakeId: null,
    rhythmTakeId: null,
    status: "SETUP",
  };
}

export function createAddPassCaptureSession(options: {
  sessionId: string;
  draft: InputPassDraft;
  tempo: number;
  grid: GridValue;
  ppq: number;
  timeSignature: MidiTimeSignature;
}): CaptureSession {
  const barTicks = ticksPerBar(options.ppq, options.timeSignature);
  const preRollBars = Math.max(0, options.draft.recordStartBar - options.draft.playbackStartBar);
  return {
    sessionId: options.sessionId,
    mode: "ADD_PASS",
    inputMethod: options.draft.inputMethod,
    countInBeats: options.draft.preRoll === "COUNT_IN"
      ? 4
      : Math.max(1, Math.round(preRollBars * options.timeSignature.numerator * 4 / options.timeSignature.denominator)),
    playbackStartTick: options.draft.playbackStartBar * barTicks,
    recordStartTick: options.draft.recordStartBar * barTicks,
    recordEndTick: options.draft.recordEndBar === null ? null : (options.draft.recordEndBar + 1) * barTicks,
    untilStop: options.draft.untilStop,
    preRollBars,
    pickupEnabled: options.draft.pickup,
    replacementRange: options.draft.replacementRange,
    tempo: options.tempo,
    grid: options.grid,
    audioTakeId: null,
    rhythmTakeId: null,
    status: "SETUP",
  };
}

export function updateCaptureSession(session: CaptureSession, patch: Partial<Omit<CaptureSession, "sessionId">>): CaptureSession {
  return { ...session, ...patch, sessionId: session.sessionId };
}

const NEXT_CAPTURE_STATUS: Record<CaptureSessionStatus, readonly CaptureSessionStatus[]> = {
  SETUP: ["COUNT_IN", "PRE_ROLL", "CANCELLED"],
  COUNT_IN: ["RECORDING", "SECOND_PASS", "CANCELLED"],
  PRE_ROLL: ["RECORDING", "SECOND_PASS", "CANCELLED"],
  RECORDING: ["STOPPING", "COMPLETE", "CANCELLED"],
  SECOND_PASS: ["STOPPING", "COMPLETE", "CANCELLED"],
  STOPPING: ["COMPLETE", "CANCELLED"],
  COMPLETE: [],
  CANCELLED: [],
};

export function transitionCaptureSession(
  session: CaptureSession,
  status: CaptureSessionStatus,
  patch: Partial<Omit<CaptureSession, "sessionId" | "status">> = {},
): CaptureSession {
  if (status === session.status) return updateCaptureSession(session, patch);
  if (!NEXT_CAPTURE_STATUS[session.status].includes(status)) {
    throw new Error(`Invalid capture transition: ${session.status} -> ${status}`);
  }
  return updateCaptureSession(session, { ...patch, status });
}

export function captureSessionSummary(session: CaptureSession, ppq: number, signature: MidiTimeSignature): string {
  const barTicks = ticksPerBar(ppq, signature);
  const playbackBar = Math.floor(session.playbackStartTick / barTicks) + 1;
  const recordBar = Math.floor(session.recordStartTick / barTicks) + 1;
  const end = session.untilStop || session.recordEndTick === null ? "STOPまで" : `BAR ${Math.max(recordBar, Math.ceil(session.recordEndTick / barTicks))}まで`;
  if (session.mode === "NEW_CAPTURE" || session.mode === "RHYTHM_ONLY") {
    return `${session.countInBeats}拍カウント → BAR ${recordBar}から${session.inputMethod === "RHYTHM" ? "TAP" : "録音"}開始`;
  }
  return `BAR ${playbackBar}から再生 → BAR ${recordBar}から${session.inputMethod}入力 → ${end}`;
}
